/**
 * Target-viewer route allowlist and home redirects (IP03 Slice 2).
 * Arbitrary ?redirect= is never honoured for this role after auth.
 */

export const TARGET_VIEWER_HOME_PATH = '/view/targets';
export const TARGET_VIEWER_CLAIM_PATH = '/auth/viewer-claim';

/** Strict post-auth allowlist for target_viewer. */
export const TARGET_VIEWER_ALLOWED_PATHS: readonly string[] = [
  TARGET_VIEWER_CLAIM_PATH,
  TARGET_VIEWER_HOME_PATH,
];

/** Portal paths that must bounce target_viewer to home (direct-route denial list). */
export const TARGET_VIEWER_DENIED_PATHS: readonly string[] = [
  '/dashboard',
  '/settings',
  '/profile',
  '/documents',
  '/growth-engine',
  '/capital-raising',
  '/capital-raising/stage-1',
  '/capital-raising/stage-2',
  '/capital-raising/stage-3',
  '/capital-raising/stage-4',
  '/capital-raising/complete',
  '/capital-raising/target-investors',
  '/admin',
  '/admin/clients',
  '/admin/login',
];

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

export function isTargetViewerAllowedPath(pathname: string): boolean {
  const p = normalizePath(pathname);
  return TARGET_VIEWER_ALLOWED_PATHS.includes(p);
}

export function isTargetViewerDeniedPath(pathname: string): boolean {
  const p = normalizePath(pathname);
  if (TARGET_VIEWER_DENIED_PATHS.includes(p)) return true;
  if (p.startsWith('/admin')) return true;
  if (p.startsWith('/capital-raising')) return true;
  if (p.startsWith('/chat')) return true;
  return false;
}

/**
 * PublicRoute: authenticated target_viewer always → /view/targets
 * (ignore arbitrary redirect query).
 */
export function resolveTargetViewerPublicRedirect(
  role: string | null | undefined,
  _redirectParam: string | null | undefined
): string | null {
  if (role === 'target_viewer') {
    return TARGET_VIEWER_HOME_PATH;
  }
  return null;
}

/**
 * ProtectedRoute / home map: target_viewer only allowlisted paths;
 * everything else forces /view/targets.
 */
export function resolveTargetViewerProtectedRedirect(
  role: string | null | undefined,
  pathname: string
): string | null {
  if (role !== 'target_viewer') return null;
  if (isTargetViewerAllowedPath(pathname)) return null;
  return TARGET_VIEWER_HOME_PATH;
}

/** Post-login / home path for any known role. */
export function getRoleHomePath(
  role: string | null | undefined,
  clientHomePath = '/dashboard'
): string {
  if (role === 'target_viewer') return TARGET_VIEWER_HOME_PATH;
  if (role === 'prospect_viewer') return '/view/prospects';
  if (role === 'admin' || role === 'super_admin') return '/admin';
  if (role === 'investor') return '/investor-onboarding';
  if (role === 'client') return clientHomePath;
  return '/auth';
}

/** Claim success / returning viewer: always force home (never honour redirect). */
export function resolveTargetViewerClaimSuccessPath(
  _redirectParam?: string | null
): string {
  return TARGET_VIEWER_HOME_PATH;
}
